import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import SmoothScroll from "@/components/SmoothScroll";
import Cursor from "@/components/Cursor";
import Loader from "@/components/Loader";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StatusBar from "@/components/StatusBar";
import BackgroundField from "@/components/BackgroundField";
import Home from "@/pages/Home";
import Assets from "@/pages/Assets";
import Pools from "@/pages/Pools";
import Vaults from "@/pages/Vaults";
import Proof from "@/pages/Proof";
import Architecture from "@/pages/Architecture";
import Docs from "@/pages/Docs";
import Roadmap from "@/pages/Roadmap";
import Token from "@/pages/Token";
import Article from "@/pages/Article";
import Faq from "@/pages/Faq";
import Risk from "@/pages/Risk";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center bg-vera-bg text-center px-6">
                    <p className="font-mono text-xs tracking-[0.25em] text-vera-gold mb-4">VERA SYSTEMS</p>
                    <h1 className="text-2xl font-bold mb-2">Something broke on the surface.</h1>
                    <p className="text-vera-mut text-sm mb-6">The layer underneath is still running. Reload to reconnect.</p>
                    <button onClick={() => window.location.reload()} data-testid="error-reload-button" className="font-mono text-xs tracking-[0.2em] border border-vera-gold/50 text-vera-gold px-6 py-3 hover:bg-vera-gold/10 transition-colors">
                        RELOAD
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function ScrollManager() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const t = setTimeout(() => {
                const el = document.querySelector(hash);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 80);
            return () => clearTimeout(t);
        }
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
}

export default function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <SmoothScroll />
                <ScrollManager />
                <div className="relative min-h-screen bg-vera-bg text-vera-white" data-testid="vera-app">
                    <div className="grain-overlay" aria-hidden="true" />
                    <BackgroundField />
                    <Cursor />
                    <Loader />
                    <Navbar />
                    <div className="relative z-[1]">
                        <main id="main">
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/assets" element={<Assets />} />
                                <Route path="/pools" element={<Pools />} />
                                <Route path="/vaults" element={<Vaults />} />
                                <Route path="/proof" element={<Proof />} />
                                <Route path="/architecture" element={<Architecture />} />
                                <Route path="/docs" element={<Docs />} />
                                <Route path="/roadmap" element={<Roadmap />} />
                                <Route path="/token" element={<Token />} />
                                <Route path="/article" element={<Article />} />
                                <Route path="/faq" element={<Faq />} />
                                <Route path="/risk" element={<Risk />} />
                                <Route path="/legal" element={<Legal />} />
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </main>
                        <StatusBar />
                        <Footer />
                    </div>
                </div>
            </BrowserRouter>
        </ErrorBoundary>
    );
}
